import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MapPin, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UI_FEEDBACK_MESSAGES } from '@/constants/uiLabels';

const emptyForm = {
  label: '',
  address: '',
  reference: '',
  district: '',
  city: '',
  phone: '',
  contact_name: '',
  is_default: false,
  address_type: 'pickup',
};

const addressTypeLabels = {
  pickup: 'Ramassage',
  delivery: 'Livraison',
  other: 'Autre',
};

const SavedAddressesSection = () => {
  const [addresses, setAddresses] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processingDefaultId, setProcessingDefaultId] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(emptyForm);

  const sortedTypeOptions = useMemo(
    () => [
      { value: 'pickup', label: addressTypeLabels.pickup },
      { value: 'delivery', label: addressTypeLabels.delivery },
      { value: 'other', label: addressTypeLabels.other },
    ],
    []
  );

  const loadAddresses = async () => {
    const response = await axios.get(`${API}/user/addresses`);
    setAddresses(response.data?.data?.items || []);
  };

  const loadCountries = async () => {
    const response = await axios.get(`${API}/countries`);
    const activeCountries = (response.data || []).filter((country) => country.is_active);
    setAllCities(
      activeCountries.flatMap((country) =>
        (country.cities || []).map((city) => ({
          ...city,
          country_code: country.code,
          country_name: country.name,
        }))
      )
    );
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadCountries(), loadAddresses()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger vos adresses enregistrees.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getDistrictsForCity = (cityCode) => {
    const city = allCities.find((item) => item.code === cityCode);
    return city?.districts || [];
  };

  const resetForm = () => {
    setEditingId('');
    setForm(emptyForm);
  };

  const handleInputChange = (event) => {
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setForm((current) => {
      if (name === 'city') {
        const nextDistricts = getDistrictsForCity(value);
        const districtStillValid = nextDistricts.includes(current.district);
        return {
          ...current,
          city: value,
          district: districtStillValid ? current.district : '',
        };
      }
      return {
        ...current,
        [name]: value,
      };
    });
  };

  const handleEdit = (address) => {
    setEditingId(address.id);
    setForm({
      label: address.label || '',
      address: address.address || '',
      reference: address.reference || '',
      district: address.district || '',
      city: address.city || '',
      phone: address.phone || '',
      contact_name: address.contact_name || '',
      is_default: Boolean(address.is_default),
      address_type: address.address_type || 'pickup',
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.label || !form.address || !form.district || !form.city) {
      toast.error('Veuillez renseigner le libelle, la ville, le district et l adresse.');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/user/addresses/${editingId}`, form);
      } else {
        await axios.post(`${API}/user/addresses`, form);
      }
      await loadAddresses();
      resetForm();
      toast.success(UI_FEEDBACK_MESSAGES.savedAddressSaved);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'ADDRESS_ALREADY_EXISTS') {
        toast.error('Cette adresse existe deja pour ce type.');
      } else {
        toast.error(detail || "Impossible d enregistrer cette adresse.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (address) => {
    if (!window.confirm(`Supprimer l adresse "${address.label}" ?`)) {
      return;
    }
    setDeletingId(address.id);
    try {
      await axios.delete(`${API}/user/addresses/${address.id}`);
      await loadAddresses();
      if (editingId === address.id) {
        resetForm();
      }
      toast.success(UI_FEEDBACK_MESSAGES.savedAddressDeleted);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de supprimer cette adresse.");
    } finally {
      setDeletingId('');
    }
  };

  const handleSetDefault = async (address) => {
    setProcessingDefaultId(address.id);
    try {
      await axios.post(`${API}/user/addresses/${address.id}/set-default`);
      await loadAddresses();
      if (editingId === address.id) {
        setForm((current) => ({ ...current, is_default: true }));
      }
      toast.success(UI_FEEDBACK_MESSAGES.savedAddressDefaultUpdated);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de definir cette adresse par defaut.");
    } finally {
      setProcessingDefaultId('');
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
        Chargement de vos adresses...
      </div>
    );
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-red-600" />
            {editingId ? 'Modifier une adresse' : 'Ajouter une adresse'}
          </CardTitle>
          <CardDescription>
            Une seule adresse par defaut est conservee par type. Le type `pickup` sert deja au formulaire de livraison.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="address_label">Libelle *</Label>
              <Input
                id="address_label"
                name="label"
                data-testid="address-label-input"
                placeholder="Boutique principale"
                value={form.label}
                onChange={handleInputChange}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Type d adresse</Label>
                <Select value={form.address_type} onValueChange={(value) => handleSelectChange('address_type', value)}>
                  <SelectTrigger data-testid="address-type-select">
                    <SelectValue placeholder="Choisissez un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="contact_name">Nom de contact</Label>
                <Input
                  id="contact_name"
                  name="contact_name"
                  placeholder="Responsable de site"
                  value={form.contact_name}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Ville *</Label>
                <Select value={form.city} onValueChange={(value) => handleSelectChange('city', value)}>
                  <SelectTrigger data-testid="address-city-select">
                    <SelectValue placeholder="Selectionnez une ville" />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    {allCities.map((city) => (
                      <SelectItem key={city.code} value={city.code}>
                        {city.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>District *</Label>
                <Select
                  value={form.district}
                  onValueChange={(value) => handleSelectChange('district', value)}
                  disabled={!form.city}
                >
                  <SelectTrigger data-testid="address-district-select">
                    <SelectValue placeholder="Selectionnez un district" />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    {getDistrictsForCity(form.city).map((district) => (
                      <SelectItem key={district} value={district}>
                        {district}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="address_value">Adresse complete *</Label>
              <Input
                id="address_value"
                name="address"
                data-testid="address-input"
                placeholder="Numero, rue, quartier..."
                value={form.address}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <Label htmlFor="address_reference">Référence</Label>
              <Input
                id="address_reference"
                name="reference"
                data-testid="address-reference-input"
                placeholder="Ex. Pharmacie X, portail noir, derrière l’église"
                value={form.reference}
                onChange={handleInputChange}
              />
              <p className="mt-1 text-xs text-slate-500">
                Repère logistique optionnel repris ensuite dans les livraisons.
              </p>
            </div>

            <div>
              <Label htmlFor="address_phone">Telephone</Label>
              <Input
                id="address_phone"
                name="phone"
                placeholder="+242..."
                value={form.phone}
                onChange={handleInputChange}
              />
            </div>

            <div className="flex items-start gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
              <Checkbox
                id="address_default"
                checked={form.is_default}
                onCheckedChange={(checked) => setForm((current) => ({ ...current, is_default: checked === true }))}
              />
              <div className="space-y-1">
                <Label htmlFor="address_default" className="cursor-pointer text-sm font-medium">
                  Definir comme adresse par defaut
                </Label>
                <p className="text-xs text-slate-600">
                  Si vous cochez cette option, les autres adresses du meme type perdront leur statut par defaut.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button type="submit" className="bg-red-500 hover:bg-red-600" disabled={saving}>
                {saving ? 'Enregistrement...' : editingId ? 'Mettre a jour' : 'Enregistrer'}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  Annuler
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-red-600" />
            Adresses enregistrees
          </CardTitle>
          <CardDescription>
            Reutilisez ces adresses dans vos futurs flux. Votre formulaire de livraison peut charger automatiquement l adresse `pickup` par defaut.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {addresses.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
              Aucune adresse enregistree pour le moment.
            </div>
          ) : (
            addresses.map((address) => (
              <div key={address.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold text-slate-900">{address.label}</h3>
                      <Badge variant="outline">{addressTypeLabels[address.address_type] || address.address_type}</Badge>
                      {address.is_default && (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                          <Star className="mr-1 h-3 w-3" />
                          Par defaut
                        </Badge>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-slate-700">{address.address}</p>
                    {address.reference && <p className="text-sm text-slate-600">Référence : {address.reference}</p>}
                    <p className="text-sm text-slate-600">
                      {address.district}, {address.city_name || address.city}
                    </p>
                    {address.contact_name && <p className="text-sm text-slate-600">Contact: {address.contact_name}</p>}
                    {address.phone && <p className="text-sm text-slate-600">Telephone: {address.phone}</p>}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {!address.is_default && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleSetDefault(address)}
                        disabled={processingDefaultId === address.id}
                      >
                        {processingDefaultId === address.id ? 'Mise a jour...' : 'Par defaut'}
                      </Button>
                    )}
                    <Button type="button" variant="outline" size="sm" onClick={() => handleEdit(address)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Modifier
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(address)}
                      disabled={deletingId === address.id}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {deletingId === address.id ? 'Suppression...' : 'Supprimer'}
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SavedAddressesSection;
